import { getSessionManagerV2 } from './sessionManagerV2.js';
import {
  isMessengerV2SendCapableGroup,
  isMessengerV2ReadOnlyGroup,
  normalizeMessengerV2ReadyInfo,
} from './groupReadiness.js';
import { fetchConversationMlsGroup, normalizeSuiteId } from './messengerV2Platform.js';

const DEFAULT_MLS_CIPHERSUITE = 0x004d;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

export const normalizeMessengerV2SuiteId = (value, fallback = DEFAULT_MLS_CIPHERSUITE) =>
  normalizeSuiteId(value) ?? fallback;

export const resolveMessengerV2Manager = ({
  manager = null,
  userId = null,
  identityName = null,
  preferredCiphersuite = DEFAULT_MLS_CIPHERSUITE,
  wantPQ = true,
  serverManaged = true,
} = {}) => {
  if (manager && typeof manager === 'object') {
    return manager;
  }

  const resolvedManager = getSessionManagerV2({
    userId,
    identityName,
    preferredCiphersuite,
    wantPQ,
    serverManaged,
  });

  try {
    if (typeof window !== 'undefined' && resolvedManager) {
      window.sessionManagerMLSV2 = resolvedManager;
    }
  } catch {}

  return resolvedManager;
};

export const resolveMessengerV2GroupRuntimeHandle = (group = null) =>
  toStringOrNull(
    group?.runtimeHandle ??
      group?.runtime_handle ??
      group?.groupHandle ??
      group?.group_handle ??
      null,
  );

export const ensureMessengerV2BootstrapKeyPackage = async ({
  manager = null,
  userId = null,
  identityName = null,
  preferredCiphersuite = DEFAULT_MLS_CIPHERSUITE,
  identityId = null,
  clientId = null,
  forcePublish = false,
} = {}) => {
  const resolvedManager = resolveMessengerV2Manager({
    manager,
    userId,
    identityName,
    preferredCiphersuite,
    wantPQ: true,
    serverManaged: true,
  });
  if (!resolvedManager) {
    throw new Error('V2 MLS manager unavailable');
  }

  const ciphersuite = normalizeMessengerV2SuiteId(preferredCiphersuite);
  const ensuredIdentity =
    (await resolvedManager.ensureIdentity?.({
      ciphersuite,
      name: identityName || undefined,
    })) ?? null;
  const normalizedIdentityId = toStringOrNull(
    identityId ?? ensuredIdentity?.identityId ?? ensuredIdentity?.identity_id ?? null,
  );
  const normalizedClientId = toStringOrNull(
    clientId ??
      ensuredIdentity?.clientId ??
      ensuredIdentity?.client_id ??
      resolvedManager.identityClientId ??
      null,
  );

  const disposition =
    (await resolvedManager.getBootstrapPublishDisposition?.({
      identityId: normalizedIdentityId,
      clientId: normalizedClientId,
      ciphersuite,
    })) ?? null;

  if (!disposition?.allowPublish) {
    return {
      skipped: true,
      deferred: Boolean(disposition?.deferred),
      reason: disposition?.reason ?? 'publish_not_required',
      identityId: disposition?.identityId ?? normalizedIdentityId,
      clientId: disposition?.clientId ?? normalizedClientId,
      ciphersuite: disposition?.ciphersuite ?? ciphersuite,
      targetRef: disposition?.targetRef ?? null,
    };
  }

  const published =
    (await resolvedManager.publishKeyPackageAndSync?.(
      disposition?.identityId ?? normalizedIdentityId,
      {
        forcePublish,
        retryOnMissingClientId: true,
      },
    )) ?? null;

  return {
    ...published,
    identityId: published?.identityId ?? disposition?.identityId ?? normalizedIdentityId,
    clientId:
      published?.clientId ??
      published?.client_id ??
      normalizedClientId ??
      resolvedManager.identityClientId ??
      null,
    ciphersuite: published?.ciphersuite ?? ciphersuite,
  };
};

export const fetchMessengerV2ConversationSnapshot = async ({
  conversationId,
  clientId = null,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }

  return fetchConversationMlsGroup({
    conversationId: normalizedConversationId,
    clientId: toStringOrNull(clientId) || undefined,
  });
};

export const applyMessengerV2Snapshot = async ({
  manager = null,
  snapshot = null,
  userId = null,
  identityName = null,
  preferredCiphersuite = DEFAULT_MLS_CIPHERSUITE,
  source = 'messages_v2',
  conversationId = null,
} = {}) => {
  if (!snapshot || typeof snapshot !== 'object') {
    return null;
  }

  const resolvedManager = resolveMessengerV2Manager({
    manager,
    userId,
    identityName,
    preferredCiphersuite,
    wantPQ: true,
    serverManaged: true,
  });

  if (!resolvedManager) {
    throw new Error('V2 MLS manager unavailable');
  }

  await resolvedManager.applyServerSnapshot?.(snapshot, {
    source,
    conversationId: toStringOrNull(conversationId),
    userId,
    identityName,
    preferredCiphersuite,
  });

  return resolvedManager;
};

export const convergeMessengerV2ReadyState = async ({
  manager = null,
  conversation = null,
  conversationId = null,
  groupId = null,
  currentUserId = null,
  currentUserName = null,
  preferredCiphersuite = DEFAULT_MLS_CIPHERSUITE,
  reason = 'messages_v2_active_thread',
  allowRecovery = true,
  allowPendingWelcomeJoin = allowRecovery !== false,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId ?? conversation?.id ?? null);
  const normalizedGroupId = toStringOrNull(
    groupId ?? conversation?.mls_group_id ?? conversation?.meta?.mls_group_id ?? null,
  );
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  if (!normalizedConversationId || !normalizedGroupId || !normalizedCurrentUserId) {
    return null;
  }

  const identityName = currentUserName || `user-${normalizedCurrentUserId}`;
  const ciphersuite = normalizeMessengerV2SuiteId(
    conversation?.mls_ciphersuite ?? conversation?.meta?.mls_ciphersuite ?? preferredCiphersuite,
  );
  const resolvedManager = resolveMessengerV2Manager({
    manager,
    userId: normalizedCurrentUserId,
    identityName,
    preferredCiphersuite: ciphersuite,
    wantPQ: true,
    serverManaged: true,
  });

  if (!resolvedManager) {
    throw new Error('V2 MLS manager unavailable');
  }

  if (typeof resolvedManager.ensureInitialized === 'function') {
    await resolvedManager.ensureInitialized();
  }

  if (typeof resolvedManager.ensureBootstrapReady === 'function') {
    await resolvedManager.ensureBootstrapReady({
      name: identityName,
      ciphersuite,
    });
  } else if (typeof resolvedManager.ensureIdentity === 'function') {
    await resolvedManager.ensureIdentity({ name: identityName, ciphersuite });
  }

  const converged =
    (await resolvedManager.convergeGroupState?.({
      groupId: normalizedGroupId,
      conversationId: normalizedConversationId,
      reason,
      allowRecovery: allowRecovery !== false,
      allowPendingWelcomeJoin: allowPendingWelcomeJoin !== false,
      preferServerSnapshot: true,
    })) ?? null;

  return {
    ensuredGroup: converged,
    converged,
    readyInfo: converged,
  };
};

export const ensureMessengerV2ThreadReadyState = async ({
  manager = null,
  conversation = null,
  conversationId = null,
  groupId = null,
  currentUserId = null,
  currentUserName = null,
  clientId = null,
  preferredCiphersuite = DEFAULT_MLS_CIPHERSUITE,
  reason = 'messages_v2_active_thread',
  waitForReady = false,
  waitTimeoutMs = 5000,
  allowRecovery = true,
  allowPendingWelcomeJoin = allowRecovery !== false,
  waitRecoveryAttempts = allowRecovery === false ? 0 : 3,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId ?? conversation?.id ?? null);
  const normalizedGroupId = toStringOrNull(
    groupId ?? conversation?.mls_group_id ?? conversation?.meta?.mls_group_id ?? null,
  );
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  if (!normalizedConversationId || !normalizedGroupId || !normalizedCurrentUserId) {
    return null;
  }

  const identityName = currentUserName || `user-${normalizedCurrentUserId}`;
  const ciphersuite = normalizeMessengerV2SuiteId(
    conversation?.mls_ciphersuite ?? conversation?.meta?.mls_ciphersuite ?? preferredCiphersuite,
  );
  const resolvedManager = resolveMessengerV2Manager({
    manager,
    userId: normalizedCurrentUserId,
    identityName,
    preferredCiphersuite: ciphersuite,
    wantPQ: true,
    serverManaged: true,
  });

  if (!resolvedManager) {
    throw new Error('V2 MLS manager unavailable');
  }

  const ensuredIdentity =
    (await resolvedManager
      .ensureIdentity?.({
        ciphersuite,
        name: identityName,
      })
      .catch(() => null)) ?? null;

  const senderClientId = toStringOrNull(
    clientId ??
      ensuredIdentity?.clientId ??
      ensuredIdentity?.client_id ??
      resolvedManager.identityClientId ??
      null,
  );
  const identityId = toStringOrNull(
    ensuredIdentity?.identityId ??
      ensuredIdentity?.identity_id ??
      resolvedManager.identityId ??
      senderClientId ??
      null,
  );

  const readyState =
    (await convergeMessengerV2ReadyState({
      manager: resolvedManager,
      conversation,
      conversationId: normalizedConversationId,
      groupId: normalizedGroupId,
      currentUserId: normalizedCurrentUserId,
      currentUserName: identityName,
      preferredCiphersuite: ciphersuite,
      reason,
      allowRecovery,
      allowPendingWelcomeJoin,
    })) ?? null;

  let readyInfo =
    readyState?.readyInfo ?? readyState?.converged ?? readyState?.ensuredGroup ?? null;

  if (
    waitForReady &&
    !isMessengerV2ReadOnlyGroup(readyInfo) &&
    !isMessengerV2SendCapableGroup(readyInfo, { clientId: senderClientId }) &&
    typeof resolvedManager.waitForGroupReady === 'function'
  ) {
    const groupHandle = toStringOrNull(readyInfo?.groupHandle ?? readyInfo?.group_handle ?? null);
    const waitedGroupInfo =
      (await resolvedManager
        .waitForGroupReady({
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          groupHandle,
          timeoutMs: waitTimeoutMs,
          allowRecovery,
          allowPendingWelcomeJoin,
          maxRecoveryAttempts: waitRecoveryAttempts,
        })
        .catch(() => null)) ?? null;

    if (waitedGroupInfo) {
      readyInfo = waitedGroupInfo;
    } else if (typeof resolvedManager.groupInfo === 'function') {
      readyInfo =
        (await resolvedManager.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) ??
        readyInfo;
    }
  }

  const normalizedReadyInfo = normalizeMessengerV2ReadyInfo(
    readyInfo,
    isMessengerV2SendCapableGroup(readyInfo, { clientId: senderClientId }),
  );

  return {
    ...readyState,
    resolvedManager,
    preferredCiphersuite: ciphersuite,
    senderClientId,
    identityId,
    readyInfo: normalizedReadyInfo,
    groupInfo: normalizedReadyInfo,
    ready: isMessengerV2SendCapableGroup(normalizedReadyInfo, { clientId: senderClientId }),
  };
};

const messengerV2MlsGateway = {
  applyMessengerV2Snapshot,
  ensureMessengerV2ThreadReadyState,
  convergeMessengerV2ReadyState,
  ensureMessengerV2BootstrapKeyPackage,
  fetchMessengerV2ConversationSnapshot,
  normalizeMessengerV2SuiteId,
  resolveMessengerV2GroupRuntimeHandle,
  resolveMessengerV2Manager,
};

export default messengerV2MlsGateway;
